<!-- app-subdomain-creators\src\accountPages\CreatorMemberTags.vue -->

<template>
  <div class="px-4 sm:px-6 lg:px-24 py-6">
    <!-- Create Tag Modal -->
    <CreatorCreateNewTagModal
      :show="showCreateModal"
      @close="showCreateModal = false"
      @create="handleCreateTagFromModal"
    />

    <!-- Delete Tag Modal -->
    <CreatorPermDeleteTag
      :isOpen="showDeleteModal"
      :tagId="tagPendingDelete ? tagPendingDelete.id : null"
      :tagName="tagPendingDelete ? tagPendingDelete.name : ''"
      @confirmDelete="handleConfirmDeleteTag"
      @close="showDeleteModal = false"
    />

    <!-- Header / Toolbar -->
    <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
      <div>
        <h1 class="text-lg font-semibold text-zinc-900">
          Member Tags
        </h1>
        <p class="mt-1 text-sm text-zinc-500">
          Create and manage tags you can assign to members in your chat profile panel.
        </p>
      </div>

      <div class="flex items-center gap-3">
        <!-- Search -->
        <div class="relative w-48 sm:w-64">
          <input
            v-model="searchQuery"
            type="text"
            placeholder="Search tags..."
            class="w-full rounded-md border border-gray-300 px-3 py-2 text-sm
                   focus:border-[#2D8CFF] focus:ring-[#2D8CFF] focus:outline-none pr-9"
          />
          <MagnifyingGlassIcon
            class="pointer-events-none absolute inset-y-2.5 right-3 h-5 w-5 text-gray-400"
          />
        </div>

        <!-- Create tag button -->
        <button
          type="button"
          class="inline-flex items-center rounded-md bg-[#2D8CFF] px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-[#1f6ecc]"
          @click="showCreateModal = true"
        >
          <PlusIcon class="w-4 h-4 mr-1.5" />
          Create tag
        </button>
      </div>
    </div>

    <!-- Messages -->
    <div class="max-w-3xl mb-3 space-y-2">
      <div v-if="loadError" class="text-sm text-red-700 bg-red-50 px-3 py-2 rounded-md">
        {{ loadError }}
      </div>
      <div v-if="successMessage" class="text-sm text-green-700 bg-green-50 px-3 py-2 rounded-md">
        {{ successMessage }}
      </div>
    </div>

    <!-- Main Card -->
    <div class="">
      <!-- Top bar inside card -->
      <div class="flex items-center justify-between px-4 py-3  border-zinc-100 text-xs text-zinc-500">
            <!-- Selected count (below card, small) -->
    <div
      v-if="totalSelected > 0"
      class="mt-3 text-xs text-zinc-500"
    >
      Selected
      <span class="font-medium text-zinc-700">{{ totalSelected }}</span>
      of
      <span class="font-medium text-zinc-700">{{ totalTags }}</span>
      tags
    </div>

        <!-- <button
          type="button"
          class="inline-flex items-center rounded-full border border-zinc-200 px-2.5 py-1 text-[11px] font-medium text-zinc-600 hover:bg-zinc-100"
          @click="reloadTags"
        >
          <ArrowPathIcon
            class="w-3.5 h-3.5 mr-1"
            :class="isLoading ? 'animate-spin' : ''"
          />
          Refresh
        </button> -->
      </div>

      <!-- Table / List -->
      <div class="relative overflow-visible">
        <!-- Loading -->
        <div v-if="isLoading" class="py-8 text-sm text-zinc-400 flex items-center justify-center gap-2">
          <ArrowPathIcon class="w-4 h-4 animate-spin" />
          Loading tags…
        </div>

        <!-- Empty -->
        <div
          v-else-if="!paginatedTags.length"
          class="py-10 text-sm text-zinc-400 text-center"
        >
          No tags found. Create your first tag with the button above.
        </div>

        <!-- Table -->
        <table
          v-else
          class="min-w-full divide-y divide-gray-200"
        >
          <thead class="">
            <tr>
              <th
                scope="col"
                class="pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6 w-12"
              >
                <div class="flex h-6 items-center">
                  <input
                    id="select-all-tags"
                    type="checkbox"
                    :checked="allSelectedOnPage"
                    @change="toggleSelectAllOnPage"
                    class="h-5 w-5 rounded border-gray-300 text-[#2D8CFF] focus:ring-[#2D8CFF]"
                  />
                </div>
              </th>
              <th
                scope="col"
                class="py-3.5 pl-2 pr-3 text-left text-xs font-semibold text-gray-900"
              >
                Name
              </th>
              <th
                scope="col"
                class="px-6 py-3.5 text-left text-xs font-semibold text-gray-900 hidden sm:table-cell"
              >
                Created
              </th>
              <th scope="col" class="relative py-3.5 pl-3 pr-4 sm:pr-6 w-12">
                <span class="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-zinc-100 bg-white">
            <tr
              v-for="tag in paginatedTags"
              :key="tag.id"
              class="hover:bg-zinc-50"
              :class="openMenuId === tag.id ? 'relative z-30' : 'relative z-0'"
            >
              <!-- Checkbox -->
              <td class="pl-4 pr-3 text-sm text-gray-900 sm:pl-6">
                <div class="flex h-6 items-center">
                  <input
                    type="checkbox"
                    class="h-5 w-5 rounded border-gray-300 text-[#2D8CFF] focus:ring-[#2D8CFF]"
                    :checked="selectedIds.includes(tag.id)"
                    @change="toggleTagSelection(tag.id, $event.target.checked)"
                  />
                </div>
              </td>

              <!-- Name -->
              <td class="whitespace-nowrap py-4 pl-2 pr-3 text-sm">
                <div class="flex items-center gap-2">
                  <span
                    class="inline-flex items-center rounded-full bg-[#2d8bff1e] px-2.5 py-0.5 text-xs font-medium text-zinc-800 max-w-[260px] truncate border border-[#2D8CFF]"
                    :title="tag.name"
                  >
                    {{ tag.name }}
                  </span>

                  <!-- <span
                    v-if="selectedIds.includes(tag.id)"
                    class="text-[11px] text-zinc-400"
                  >
                    Selected
                  </span> -->
                </div>
              </td>

              <!-- Created -->
              <td class="whitespace-nowrap px-6 py-4 text-sm text-zinc-500 hidden sm:table-cell">
                <span v-if="tag.createdAtFormatted">
                  {{ tag.createdAtFormatted }}
                </span>
                <span v-else class="text-zinc-400 text-xs">
                  —
                </span>
              </td>

              <!-- Row actions -->
              <td class="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                <div class="relative user-dropdown w-9 h-9 p-1.5 hover:bg-zinc-100 rounded-full flex items-center justify-center">
                  <EllipsisVerticalIcon
                    class="h-6 w-6 text-zinc-500 cursor-pointer"
                    @click.stop="toggleRowMenu(tag.id)"
                  />

                  <div
                    v-if="openMenuId === tag.id"
                    class="absolute z-50 right-9 top-0 mt-1 w-44 bg-white border border-zinc-200 rounded-md shadow-lg text-left"
                  >
                    <ul class="py-1 text-sm">
                      <li>
                        <button
                          type="button"
                          class="block w-full text-left px-4 py-2 text-zinc-700 hover:bg-zinc-100"
                          @click.stop="handleRowAction('showMembers', tag)"
                        >
                          Show members
                        </button>
                      </li>
                      <li>
                        <button
                          type="button"
                          class="block w-full text-left px-4 py-2 text-red-600 hover:bg-red-50"
                          @click.stop="handleRowAction('delete', tag)"
                        >
                          Delete tag
                        </button>
                      </li>
                    </ul>
                  </div>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Bottom bar / pagination -->
      <div
        v-if="totalTags > 0"
        class="flex flex-col gap-3 border-t border-zinc-100 px-4 py-5 text-sm text-zinc-600 sm:flex-row sm:items-center sm:justify-between"
      >
        <div>
          Showing
          <span class="font-medium">{{ startIndex }}</span>
          to
          <span class="font-medium">{{ endIndex }}</span>
          of
          <span class="font-medium">{{ totalTags }}</span>
          results
        </div>

        <div class="flex flex-wrap items-center justify-end gap-4">
          <label class="flex items-center gap-2 text-sm text-gray-700">
            <span>Rows per page:</span>
            <PlatformaSelect
              v-model.number="itemsPerPage"
              class="min-w-[110px]"
              :options="rowsPerPageOptions"
              menu-placement="top"
            />
          </label>
          <div class="text-sm text-gray-700">
            Page
            <span class="font-medium">{{ currentPage }}</span>
            of
            <span class="font-medium">{{ totalPages }}</span>
          </div>
          <div class="flex gap-2">
            <button
              type="button"
              @click="prevPage"
              :disabled="currentPage === 1"
              class="relative inline-flex items-center px-[10px] py-[6px] border border-gray-300 text-xs font-medium rounded-lg text-gray-700 bg-white hover:text-gray-500 focus:outline-none focus:ring-blue focus:border-[#2D8CFF] active:bg-gray-100 active:text-gray-700 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <button
              type="button"
              @click="nextPage"
              :disabled="currentPage >= totalPages"
              class="relative inline-flex items-center px-[10px] py-[6px] border border-gray-300 text-xs font-medium rounded-lg text-gray-700 bg-white hover:text-gray-500 focus:outline-none focus:ring-blue focus:border-[#2D8CFF] active:bg-gray-100 active:text-gray-700 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>




  </div>
</template>

<script setup>
import {
  ref,
  computed,
  onMounted,
  onBeforeUnmount,
  watch
} from 'vue'
import {
  PlusIcon,
  ArrowPathIcon,
  EllipsisVerticalIcon,
  MagnifyingGlassIcon
} from '@heroicons/vue/24/outline'
import CreatorCreateNewTagModal from '@/Components/CreatorCreateNewTagModal.vue'
import CreatorPermDeleteTag from '@/Components/CreatorPermDeleteTag.vue'
import PlatformaSelect from '@/Components/PlatformaSelect.vue'

const apiProxyUrl = process.env.VUE_APP_API_PROXY_URL

// state
const tags = ref([])              // normalized tags from backend
const isLoading = ref(false)
const isCreating = ref(false)
const loadError = ref('')
const successMessage = ref('')

const showCreateModal = ref(false)
const searchQuery = ref('')

// delete modal state
const showDeleteModal = ref(false)
const tagPendingDelete = ref(null)

// selection + row menu
const selectedIds = ref([])       // array of tag IDs
const openMenuId = ref(null)

// pagination
const currentPage = ref(1)
const itemsPerPage = ref(6)
const rowsPerPageOptions = Object.freeze([6, 12, 24, 50, 100, 500].map((value) => ({
  value,
  label: String(value)
})))

// --- helpers ---
function normalizeTag(raw) {
  if (!raw || typeof raw !== 'object') return null

  const createdAtRaw = raw.createdAt ?? raw.created_at ?? null
  let createdAtFormatted = ''

  if (createdAtRaw) {
    const d = new Date(createdAtRaw)
    if (!isNaN(d.getTime())) {
      createdAtFormatted = d.toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      })
    }
  }

  return {
    id: raw.id ?? raw.tag_id,
    name: raw.name ?? raw.tag_name ?? '',
    creatorId: raw.creatorId ?? raw.creator_id ?? null,
    createdAt: createdAtRaw,
    createdAtFormatted
  }
}

// --- computed lists ---
const filteredTags = computed(() => {
  const q = searchQuery.value.trim().toLowerCase()
  if (!q) return tags.value
  return tags.value.filter(t =>
    (t.name || '').toLowerCase().includes(q)
  )
})

const sortedTags = computed(() => {
  // newest first by createdAt; fallback to name
  return [...filteredTags.value].sort((a, b) => {
    const aDate = a.createdAt ? new Date(a.createdAt).getTime() : 0
    const bDate = b.createdAt ? new Date(b.createdAt).getTime() : 0
    if (bDate !== aDate) return bDate - aDate
    return (a.name || '').localeCompare(b.name || '')
  })
})

const totalTags = computed(() => sortedTags.value.length)

const paginatedTags = computed(() => {
  const start = (currentPage.value - 1) * itemsPerPage.value
  const end = start + itemsPerPage.value
  return sortedTags.value.slice(start, end)
})

const totalPages = computed(() =>
  Math.max(1, Math.ceil(totalTags.value / itemsPerPage.value))
)

const startIndex = computed(() =>
  totalTags.value === 0 ? 0 : (currentPage.value - 1) * itemsPerPage.value + 1
)
const endIndex = computed(() =>
  Math.min(currentPage.value * itemsPerPage.value, totalTags.value)
)

watch([searchQuery, itemsPerPage], () => {
  currentPage.value = 1
  openMenuId.value = null
})

watch(totalPages, (nextTotalPages) => {
  if (currentPage.value > nextTotalPages) currentPage.value = nextTotalPages
})

// selection
const visibleIds = computed(() => paginatedTags.value.map(t => t.id))

const allSelectedOnPage = computed(() =>
  visibleIds.value.length > 0 &&
  visibleIds.value.every(id => selectedIds.value.includes(id))
)

const totalSelected = computed(() => selectedIds.value.length)

// --- API calls ---
async function fetchTags() {
  isLoading.value = true
  loadError.value = ''
  try {
    const res = await fetch(`${apiProxyUrl}/api/creator-tags`, {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json'
      }
    })

    if (!res.ok) {
      const text = await res.text().catch(() => '')
      console.error('[CreatorMemberTags] fetchTags non-OK:', res.status, text)
      throw new Error('Failed to load tags')
    }

    const data = await res.json().catch(() => ({}))
    const rawTags = data.tags || data.items || data.results || []

    tags.value = rawTags.map(normalizeTag).filter(Boolean)
    // reset selection if list changed
    selectedIds.value = []
    if (currentPage.value > totalPages.value) {
      currentPage.value = totalPages.value
    }
  } catch (err) {
    console.error('[CreatorMemberTags] fetchTags error:', err)
    loadError.value = err && err.message ? err.message : 'Error loading tags'
  } finally {
    isLoading.value = false
  }
}

// function reloadTags() {
//   fetchTags()
// }

// create tag (called from modal)
async function handleCreateTagFromModal(name) {
  const trimmed = (name || '').trim()
  if (!trimmed || isCreating.value) return

  isCreating.value = true
  loadError.value = ''
  successMessage.value = ''

  try {
    const res = await fetch(`${apiProxyUrl}/api/creator-tags`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ tagName: trimmed })
    })

    const data = await res.json().catch(() => ({}))

    if (!res.ok) {
      console.error('[CreatorMemberTags] create tag non-OK:', res.status, data)
      throw new Error(data.message || data.error || 'Failed to create tag')
    }

    const created = normalizeTag(data.tag || data)
    if (created) {
      const idx = tags.value.findIndex(t => t.id === created.id)
      if (idx === -1) {
        tags.value.push(created)
      } else {
        tags.value[idx] = created
      }
    }

    showCreateModal.value = false
    successMessage.value = data.duplicate
      ? 'Tag already existed and is available.'
      : 'Tag created successfully.'
  } catch (err) {
    console.error('[CreatorMemberTags] createTag error:', err)
    loadError.value = err && err.message ? err.message : 'Error creating tag'
  } finally {
    isCreating.value = false
    if (successMessage.value) {
      setTimeout(() => {
        successMessage.value = ''
      }, 3000)
    }
  }
}

// delete
async function deleteTag(tag) {
  if (!tag || !tag.id) return

  try {
    const res = await fetch(
      `${apiProxyUrl}/api/creator-tags/${encodeURIComponent(tag.id)}`,
      {
        method: 'DELETE',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      }
    )

    const body = await res.json().catch(() => ({}))

    if (!res.ok) {
      console.error('[CreatorMemberTags] delete tag non-OK:', res.status, body)
      throw new Error(body.message || body.error || 'Failed to delete tag')
    }

    tags.value = tags.value.filter(t => t.id !== tag.id)
    selectedIds.value = selectedIds.value.filter(id => id !== tag.id)

    successMessage.value = 'Tag deleted.'
  } catch (err) {
    console.error('[CreatorMemberTags] deleteTag error:', err)
    loadError.value = err && err.message ? err.message : 'Error deleting tag'
  } finally {
    if (successMessage.value) {
      setTimeout(() => {
        successMessage.value = ''
      }, 2000)
    }
  }
}

// called by delete modal
function handleConfirmDeleteTag(tagId) {
  const tag =
    tags.value.find(t => t.id === tagId) ||
    tagPendingDelete.value ||
    null

  if (tag) {
    deleteTag(tag)
  }

  showDeleteModal.value = false
  tagPendingDelete.value = null
}

// --- UI actions ---
function toggleTagSelection(id, checked) {
  if (checked) {
    if (!selectedIds.value.includes(id)) {
      selectedIds.value = [...selectedIds.value, id]
    }
  } else {
    selectedIds.value = selectedIds.value.filter(x => x !== id)
  }
}

function toggleSelectAllOnPage() {
  if (allSelectedOnPage.value) {
    // unselect all visible
    const visibleSet = new Set(visibleIds.value)
    selectedIds.value = selectedIds.value.filter(id => !visibleSet.has(id))
  } else {
    // select all visible
    const set = new Set(selectedIds.value)
    visibleIds.value.forEach(id => set.add(id))
    selectedIds.value = Array.from(set)
  }
}

function toggleRowMenu(tagId) {
  openMenuId.value = openMenuId.value === tagId ? null : tagId
}

function handleRowAction(action, tag) {
  openMenuId.value = null

  if (action === 'delete') {
    tagPendingDelete.value = tag
    showDeleteModal.value = true
  } else if (action === 'showMembers') {
    // TODO: wire this later (e.g., navigate to a filtered member list)
    console.log('[CreatorMemberTags] Show members for tag:', tag)
    alert('Show members for this tag – to be wired later.')
  }
}

// pagination
function nextPage() {
  if (currentPage.value < totalPages.value) {
    currentPage.value++
  }
}

function prevPage() {
  if (currentPage.value > 1) {
    currentPage.value--
  }
}

// click outside to close row menus
function handleClickOutside(event) {
  const dropdowns = document.querySelectorAll('.user-dropdown')
  let clickedInside = false

  dropdowns.forEach((el) => {
    if (el.contains(event.target)) {
      clickedInside = true
    }
  })

  if (!clickedInside) {
    openMenuId.value = null
  }
}

onMounted(() => {
  fetchTags()
  document.addEventListener('click', handleClickOutside)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script>


